// src/render/audio/stingers.ts — Phase 6 stinger one-shot pool (06-06).
//
// CONTEXT D-23 + AUDIO-05: covers torch-on/off, catch, base-touch, free-all,
// motion-light-hum (loop), fear-gasp-afraid, fear-gasp-terrified. The frozen
// list of stingers lives in `./stinger-registry.ts` (06-02). Pool size per
// Howl is P6_HOWL_POOL_STINGER (D-20). One-shot eviction on overflow.
//
// motion-light-hum is the only loop entry (D-23 + VIS-05 alignment):
//   - playStinger('motion-light-hum') fades 0 → MOTION_HUM_VOLUME over 80 ms
//     when the light arms.
//   - stopMotionHum() fades MOTION_HUM_VOLUME → 0 over 400 ms and stops the
//     in-flight loop after the fade completes.
//   - The motionHumPlaybackId guard prevents stacking multiple loops if
//     motion-light-on flips repeatedly (T-6-06-01 mitigate).
//
// BLOCKER-1 (Plan 03 trackHowl contract): every constructed stinger Howl
// (8 total per STINGERS registry) is registered with audioManager.trackHowl
// so liveSources reflects reality (Plan 03 contract; AUDIO-07 ≤ 8 ceiling
// proof in 06-08). Cycle-safe at runtime — see ambient.ts header for the
// same pattern.
//
// Source: 06-CONTEXT.md D-23; 06-PATTERNS.md `src/render/audio/stingers.ts`;
//         06-RESEARCH.md D-23 motion-light-hum fade-in 80ms / fade-out 400ms.

import { Howl } from 'howler';

import type { AudioCodec, AudioManifest } from './codec';
import { srcListFor } from './codec';
import { STINGERS, type StingerName } from './stinger-registry';
import { P6_HOWL_POOL_STINGER } from './constants';
import { audioManager } from './manager';

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

const stingerHowls: Partial<Record<StingerName, Howl>> = {};

/**
 * In-flight motion-hum playback id. The hum loop must NOT stack — repeated
 * motion-light-on events while the hum is already humming should be a no-op
 * (T-6-06-01 mitigate). Stored in a wrapper so we can mutate `id` without
 * reassigning the const reference.
 */
const motionHumPlaybackId: { id: number | null } = { id: null };

// D-23: VIS-05 alignment timings.
const MOTION_HUM_FADE_IN_MS = 80;
const MOTION_HUM_FADE_OUT_MS = 400;
const MOTION_HUM_VOLUME = 0.3;

// ---------------------------------------------------------------------------
// Public API (matches 06-03 manager.ts seam)
// ---------------------------------------------------------------------------

export function initStingers(_codec: AudioCodec, manifest: AudioManifest): void {
  for (const name of Object.keys(STINGERS) as StingerName[]) {
    const cfg = STINGERS[name];
    // D-20 split — looping motion-hum is the only entry forced to pool=1
    // (it never stacks, T-6-06-01 mitigate). Every other stinger is a
    // one-shot whose pool is the literal P6_HOWL_POOL_STINGER constant
    // (single grep target for retune at P7 POL-06).
    if (cfg.loop) {
      stingerHowls[name] = new Howl({
        src: srcListFor(cfg.manifestKey, manifest),
        loop: true,
        pool: 1,
        preload: false,
        volume: 0,
      });
    } else {
      stingerHowls[name] = new Howl({
        src: srcListFor(cfg.manifestKey, manifest),
        loop: false,
        pool: P6_HOWL_POOL_STINGER,
        preload: false,
        volume: 0.7,
      });
    }
    stingerHowls[name]!.load();
    // BLOCKER-1: register every stinger Howl (8 total) with manager
    // liveSources Set (Plan 03 trackHowl contract). UI-06: stingers are SFX at
    // the one-shot design volume 0.7 (the motion-hum loop drives its own per-id
    // fade volume, so the group gain only matters for the one-shots).
    audioManager.trackHowl(stingerHowls[name]!, 'sfx', 0.7);
  }
}

/**
 * Play a one-shot stinger or arm the motion-light-hum loop. The motion-hum
 * branch is the only special case: it's a fade-in loop, not a one-shot.
 */
export function playStinger(name: StingerName): void {
  const howl = stingerHowls[name];
  if (!howl) return;

  if (name === 'motion-light-hum') {
    // T-6-06-01: skip if already humming (idempotent guard).
    if (motionHumPlaybackId.id !== null) return;
    howl.volume(0);
    motionHumPlaybackId.id = howl.play();
    howl.fade(0, MOTION_HUM_VOLUME, MOTION_HUM_FADE_IN_MS, motionHumPlaybackId.id);
    return;
  }

  // One-shot stinger — Howler pool handles concurrent eviction.
  howl.play();
}

/**
 * Wired by manager.ts on the renderBus 'motion-light-off' subscription.
 * Fades the in-flight hum to silence and schedules a stop after the fade
 * completes. Re-armable: after the stop fires, motionHumPlaybackId is null
 * so a subsequent motion-light-on can play the hum again.
 */
export function stopMotionHum(): void {
  const howl = stingerHowls['motion-light-hum'];
  if (!howl || motionHumPlaybackId.id === null) return;
  const currentId = motionHumPlaybackId.id;
  howl.fade(MOTION_HUM_VOLUME, 0, MOTION_HUM_FADE_OUT_MS, currentId);
  setTimeout(() => howl.stop(currentId), MOTION_HUM_FADE_OUT_MS);
  motionHumPlaybackId.id = null;
}

export function destroyStingers(): void {
  for (const name of Object.keys(stingerHowls) as StingerName[]) {
    stingerHowls[name]?.unload();
    delete stingerHowls[name];
  }
  motionHumPlaybackId.id = null;
}
